import React, { useState, useEffect, useRef, useCallback } from 'react'

// Libraries
import { Col, Container, Row } from 'react-bootstrap'
import { useParams } from 'react-router-dom'

// Components
import PortfolioOverlay from '../Components/Portfolio/PortfolioOverlay'
import RDPHeader from '../Components/Header/RDPHeader'
import RDPFooter from '../Components/Footers/RDPFooter'

// Connexion Supabase
import { supabase } from '../lib/supabaseClient'
import { getProductsFromCache, setProductsCache } from '../lib/productsCache'

// Constantes pour l'infinite scroll
const PRODUCTS_PER_PAGE = 24
const LOAD_MORE_THRESHOLD = 200 // pixels avant la fin pour déclencher le chargement

// Fonction de tri : 1) capacité ras bord croissante, 2) hauteur croissante
const sortProductsData = (products) => {
  return [...products].sort((a, b) => {
    // Utiliser capacity (ras bord) en priorité - c'est le contenu réel
    const capA = parseInt(a.productData?.capacity) || 0
    const capB = parseInt(b.productData?.capacity) || 0
    // Critère primaire : capacité ras bord
    if (capA !== capB) return capA - capB
    // Critère secondaire : hauteur (pour même capacité)
    const heightA = parseFloat(a.productData?.height) || 0
    const heightB = parseFloat(b.productData?.height) || 0
    return heightA - heightB
  })
}

const ProductsPage = (props) => {
  // Récupérer la catégorie depuis l'URL
  const { categorie } = useParams()

  // Charger le cache une seule fois au montage ET appliquer le tri
  const initialCache = React.useMemo(() => {
    const cache = getProductsFromCache()
    if (cache?.formatted && cache?.metadata) {
      // Trier formatted et synchroniser metadata dans le même ordre
      const sortedFormatted = sortProductsData(cache.formatted)
      // Recréer metadata dans le même ordre que sortedFormatted (via les IDs)
      const idToMetadata = new Map(cache.metadata.map(m => [m.id, m]))
      cache.metadata = sortedFormatted.map(p => idToMetadata.get(p.id))
      cache.formatted = sortedFormatted
    }
    return cache
  }, [])

  // State pour les produits - initialiser avec le cache si disponible
  const [products, setProducts] = useState(initialCache?.formatted || [])
  // State pour les métadonnées - initialiser avec le cache si disponible
  const [productsMetadata, setProductsMetadata] = useState(initialCache?.metadata || [])
  // Loading est false si on a du cache, true sinon
  const [loading, setLoading] = useState(!initialCache)

  // Infinite scroll states
  const [displayCount, setDisplayCount] = useState(PRODUCTS_PER_PAGE)
  const [loadingMore, setLoadingMore] = useState(false)
  const loaderRef = useRef(null)

  // Données de démo
  const initDemoData = () => {
    const demoMeta = [
      { id: 1, capacity: "750", colors: ["Antique"], categories: ["vin"] },
      { id: 2, capacity: "750", colors: ["Antique"], categories: ["vin"] },
      { id: 3, capacity: "330", colors: ["Brun"], categories: ["biere"] },
      { id: 4, capacity: "750", colors: ["Brun"], categories: ["biere"] },
      { id: 5, capacity: "700", colors: ["Blanc"], categories: ["spiritueux"] },
      { id: 6, capacity: "1000", colors: ["Blanc"], categories: ["spiritueux"] },
      { id: 7, capacity: "500", colors: ["Blanc"], categories: ["bocaux"] },
      { id: 8, capacity: "370", colors: ["Blanc"], categories: ["bocaux"] },
      { id: 9, capacity: "250", colors: ["Blanc"], categories: ["soft-drinks"] },
      { id: 10, capacity: "1000", colors: ["Blanc"], categories: ["soft-drinks"] },
      { id: 11, capacity: "500", colors: ["Transparent"], categories: ["pet"] },
      { id: 12, capacity: "1500", colors: ["Transparent"], categories: ["pet"] }
    ]

    const demoProd = [
      {
        id: 1,
        title: "Bouteille Bordeaux 75cl",
        img: require("../Assets/img/markets/vin.jpg"),
        category: ["vin"],
        subtitle: "750 ml - Liège",
        link: "/produit/demo-bordeaux-75cl",
        double_col: false,
        productData: { id: 1, name: "Bouteille Bordeaux 75cl", code: "BOR750", category: "vin", capacity: 750, color: "Antique", cap: "Liège", image_url: require("../Assets/img/markets/vin.jpg"), slug: "demo-bordeaux-75cl" }
      },
      {
        id: 2,
        title: "Bouteille Bourgogne 75cl",
        img: require("../Assets/img/markets/biere.jpg"),
        category: ["vin"],
        subtitle: "750 ml - Liège",
        link: "/produit/demo-bourgogne-75cl",
        double_col: false,
        productData: { id: 2, name: "Bouteille Bourgogne 75cl", code: "BOU750", category: "vin", capacity: 750, color: "Antique", cap: "Liège", image_url: require("../Assets/img/markets/biere.jpg"), slug: "demo-bourgogne-75cl" }
      },
      {
        id: 3,
        title: "Bouteille Bière 33cl",
        img: require("../Assets/img/markets/pet.jpg"),
        category: ["biere"],
        subtitle: "330 ml - Capsule",
        link: "/produit/demo-biere-33cl",
        double_col: false,
        productData: { id: 3, name: "Bouteille Bière 33cl", code: "BIE330", category: "biere", capacity: 330, color: "Brun", cap: "Capsule", image_url: require("../Assets/img/markets/pet.jpg"), slug: "demo-biere-33cl" }
      },
      {
        id: 4,
        title: "Bouteille Bière 75cl",
        img: require("../Assets/img/markets/vin.jpg"),
        category: ["biere"],
        subtitle: "750 ml - Mécanique",
        link: "/produit/demo-biere-75cl",
        double_col: false,
        productData: { id: 4, name: "Bouteille Bière 75cl", code: "BIE750", category: "biere", capacity: 750, color: "Brun", cap: "Mécanique", image_url: require("../Assets/img/markets/vin.jpg"), slug: "demo-biere-75cl" }
      },
      {
        id: 5,
        title: "Flacon Spiritueux 70cl",
        img: require("../Assets/img/markets/biere.jpg"),
        category: ["spiritueux"],
        subtitle: "700 ml - Bouchon GL",
        link: "/produit/demo-spiritueux-70cl",
        double_col: false,
        productData: { id: 5, name: "Flacon Spiritueux 70cl", code: "SPI700", category: "spiritueux", capacity: 700, color: "Blanc", cap: "Bouchon GL", image_url: require("../Assets/img/markets/biere.jpg"), slug: "demo-spiritueux-70cl" }
      },
      {
        id: 6,
        title: "Bouteille Vodka 100cl",
        img: require("../Assets/img/markets/pet.jpg"),
        category: ["spiritueux"],
        subtitle: "1000 ml - Bouchon GL",
        link: "/produit/demo-vodka-100cl",
        double_col: false,
        productData: { id: 6, name: "Bouteille Vodka 100cl", code: "VOD1000", category: "spiritueux", capacity: 1000, color: "Blanc", cap: "Bouchon GL", image_url: require("../Assets/img/markets/pet.jpg"), slug: "demo-vodka-100cl" }
      },
      {
        id: 7,
        title: "Bocal Conserve 500ml",
        img: require("../Assets/img/markets/vin.jpg"),
        category: ["bocaux"],
        subtitle: "500 ml - TO82",
        link: "/produit/demo-bocal-500ml",
        double_col: false,
        productData: { id: 7, name: "Bocal Conserve 500ml", code: "BOC500", category: "bocaux", capacity: 500, color: "Blanc", cap: "TO82", image_url: require("../Assets/img/markets/vin.jpg"), slug: "demo-bocal-500ml" }
      },
      {
        id: 8,
        title: "Bocal Confiture 370ml",
        img: require("../Assets/img/markets/biere.jpg"),
        category: ["bocaux"],
        subtitle: "370 ml - TO63",
        link: "/produit/demo-confiture-370ml",
        double_col: false,
        productData: { id: 8, name: "Bocal Confiture 370ml", code: "BOC370", category: "bocaux", capacity: 370, color: "Blanc", cap: "TO63", image_url: require("../Assets/img/markets/biere.jpg"), slug: "demo-confiture-370ml" }
      },
      {
        id: 9,
        title: "Bouteille Soda 25cl",
        img: require("../Assets/img/markets/pet.jpg"),
        category: ["soft-drinks"],
        subtitle: "250 ml - Capsule",
        link: "/produit/demo-soda-25cl",
        double_col: false,
        productData: { id: 9, name: "Bouteille Soda 25cl", code: "SOD250", category: "soft-drinks", capacity: 250, color: "Blanc", cap: "Capsule", image_url: require("../Assets/img/markets/pet.jpg"), slug: "demo-soda-25cl" }
      },
      {
        id: 10,
        title: "Bouteille Jus 1L",
        img: require("../Assets/img/markets/vin.jpg"),
        category: ["soft-drinks"],
        subtitle: "1000 ml - TO48",
        link: "/produit/demo-jus-1l",
        double_col: false,
        productData: { id: 10, name: "Bouteille Jus 1L", code: "JUS1000", category: "soft-drinks", capacity: 1000, color: "Blanc", cap: "TO48", image_url: require("../Assets/img/markets/vin.jpg"), slug: "demo-jus-1l" }
      },
      {
        id: 11,
        title: "Bouteille PET 50cl",
        img: require("../Assets/img/markets/biere.jpg"),
        category: ["pet"],
        subtitle: "500 ml - Plastique",
        link: "/produit/demo-pet-50cl",
        double_col: false,
        productData: { id: 11, name: "Bouteille PET 50cl", code: "PET500", category: "pet", capacity: 500, color: "Transparent", cap: "Plastique", image_url: require("../Assets/img/markets/biere.jpg"), slug: "demo-pet-50cl" }
      },
      {
        id: 12,
        title: "Bouteille PET 1.5L",
        img: require("../Assets/img/markets/pet.jpg"),
        category: ["pet"],
        subtitle: "1500 ml - Plastique",
        link: "/produit/demo-pet-15l",
        double_col: false,
        productData: { id: 12, name: "Bouteille PET 1.5L", code: "PET1500", category: "pet", capacity: 1500, color: "Transparent", cap: "Plastique", image_url: require("../Assets/img/markets/pet.jpg"), slug: "demo-pet-15l" }
      }
    ]

    setProductsMetadata(demoMeta)
    setProducts(demoProd)
  }

  // Fetch products et initialiser le filtre selon la catégorie
  useEffect(() => {
    // Scroll to top lors du changement de catégorie
    window.scrollTo(0, 0)

    fetchProducts()
    // Initialiser le filtre type avec la catégorie de l'URL
    if (categorie) {
      setFilters(prev => ({
        ...prev,
        type: categorie === 'tout' ? '*' : categorie
      }))
    }
    // Reset displayCount quand on change de catégorie
    setDisplayCount(PRODUCTS_PER_PAGE)
  }, [categorie])

  // Infinite scroll - IntersectionObserver
  const loadMoreProducts = useCallback(() => {
    setLoadingMore(true)
    // Petit délai pour l'animation du loader
    setTimeout(() => {
      setDisplayCount(prev => prev + PRODUCTS_PER_PAGE)
      setLoadingMore(false)
    }, 300)
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0]
        if (target.isIntersecting && !loadingMore && !loading) {
          loadMoreProducts()
        }
      },
      {
        rootMargin: `${LOAD_MORE_THRESHOLD}px`,
        threshold: 0
      }
    )

    if (loaderRef.current) {
      observer.observe(loaderRef.current)
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current)
      }
    }
  }, [loadMoreProducts, loadingMore, loading])

  const fetchProducts = async () => {
    try {
      // 1. Si on a déjà des produits (du cache initial), pas besoin de refetch
      if (products.length > 0) {
        return;
      }

      // 2. Essayer de charger depuis le cache
      const cachedProducts = getProductsFromCache();
      if (cachedProducts) {
        setProductsMetadata(cachedProducts.metadata);
        setProducts(cachedProducts.formatted);
        setLoading(false);
        return;
      }

      // 3. Pas de cache - affiche le loading et charge depuis Supabase
      setLoading(true)
      const { data, error } = await supabase
        .from('products')
        .select('*')

      if (error) throw error

      if (data && data.length > 0) {
        // Tri côté client : 1) capacité ras bord croissante, 2) hauteur croissante
        data.sort((a, b) => {
          const capA = parseInt(a.capacity) || 0
          const capB = parseInt(b.capacity) || 0
          if (capA !== capB) return capA - capB
          const heightA = parseFloat(a.height) || 0
          const heightB = parseFloat(b.height) || 0
          return heightA - heightB
        })

        // Métadonnées pour filtrage
        const metadata = data.map(product => ({
          id: product.id,
          capacity: product.capacity,
          colors: product.colors || (product.color ? [product.color] : []),
          // Support multi-catégories: utilise categories si disponible, sinon fallback sur category
          categories: product.categories || (product.category ? [product.category] : [])
        }))
        setProductsMetadata(metadata)

        // Données formatées pour PortfolioOverlay
        const formattedData = data.map(product => ({
          id: product.id,
          title: product.name,
          img: product.image_url || 'https://placehold.co/800x650/cccccc/333333',
          // Support multi-catégories
          category: product.categories || (product.category ? [product.category] : []),
          subtitle: `${product.capacity || ''} ml - ${product.cap || ''}`.trim(),
          link: `/produit/${product.slug || product.id}`,
          double_col: false,
          productData: product
        }))
        setProducts(formattedData)

        // 3. Sauvegarder dans le cache
        setProductsCache({
          metadata,
          formatted: formattedData
        });
      } else {
        initDemoData()
      }
    } catch {
      initDemoData()
    } finally {
      setLoading(false)
    }
  }

  // État des filtres
  const [filters, setFilters] = useState({
    type: '*',
    capacityMin: 0,
    capacityMax: 2000,
    color: '*'
  })

  // Valeurs min/max pour le slider
  const [capacityRange, setCapacityRange] = useState([0, 2000])

  // État des accordéons de filtres - Fermés par défaut
  const [filterAccordions, setFilterAccordions] = useState({
    category: false,
    color: false,
    capacity: false
  })

  const toggleFilterAccordion = (key) => {
    setFilterAccordions(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }
  

  // Filtrer les produits
  const getFilteredProducts = () => {
    const indices = []
    productsMetadata.forEach((metadata, index) => {
      // Filtre par type/catégorie (supporte les multi-catégories)
      if (filters.type !== '*' && (!metadata.categories || !metadata.categories.includes(filters.type))) {
        return
      }

      // Filtre par contenance (range slider)
      const capacity = parseInt(metadata.capacity) || 0
      if (capacity < capacityRange[0] || capacity > capacityRange[1]) {
        return
      }

      // Filtre par couleur (supporte les multi-couleurs)
      if (filters.color !== '*' && (!metadata.colors || !metadata.colors.includes(filters.color))) {
        return
      }

      indices.push(index)
    })

    return indices.map(i => products[i])
  }

  const filteredProducts = getFilteredProducts()

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }))
    // Reset infinite scroll quand on change de filtre
    setDisplayCount(PRODUCTS_PER_PAGE)
  }

  const resetFilters = () => {
    setFilters({
      type: categorie === 'tout' ? '*' : categorie || '*',
      capacityMin: 0,
      capacityMax: 2000,
      color: '*'
    })
    setCapacityRange([0, 2000])
    setDisplayCount(PRODUCTS_PER_PAGE)
  }

  // Fonction pour obtenir le titre de la catégorie
  const getCategoryTitle = () => {
    const titles = {
      'tout': 'Tous nos produits',
      'vin': 'Bouteilles de vin',
      'biere': 'Bouteilles de bière',
      'spiritueux': 'Spiritueux',
      'bocaux': 'Bocaux',
      'soft-drinks': 'Soft drinks',
      'huiles-sauces': 'Huiles et Sauces',
      'pet': 'Bouteilles en PET',
      'decorees': 'Bouteilles décorées'
    }
    return titles[categorie] || 'Nos produits'
  }

  // Compter les filtres actifs
  const activeFiltersCount =
    (filters.color !== '*' ? 1 : 0) +
    (capacityRange[0] !== 0 || capacityRange[1] !== 2000 ? 1 : 0) +
    (categorie === 'tout' && filters.type !== '*' ? 1 : 0)

  // Gérer le changement du slider
  const handleCapacityRangeChange = (e, index) => {
    const newRange = [...capacityRange]
    newRange[index] = parseInt(e.target.value)

    // S'assurer que min <= max
    if (index === 0 && newRange[0] > newRange[1]) {
      newRange[0] = newRange[1]
    }
    if (index === 1 && newRange[1] < newRange[0]) {
      newRange[1] = newRange[0]
    }

    setCapacityRange(newRange)
    setDisplayCount(PRODUCTS_PER_PAGE)
  }

  // Produits à afficher (limités par infinite scroll)
  const displayedProducts = filteredProducts.slice(0, displayCount)
  const hasMoreProducts = displayCount < filteredProducts.length

  return (
    <div style={props.style}>
      {/* Header Start */}
      <RDPHeader {...props} />
      {/* Header End */}

      {/* Hero Section */}
      <section className="pt-[130px] pb-[40px] lg:pt-[110px] lg:pb-[30px] md:pt-[100px] md:pb-[25px] sm:pt-[100px] bg-gradient-to-b from-glass-pearl to-white">
        <Container>
          <Row className="justify-center">
            <Col lg={8} className="text-center">
              <h1 className="font-serif font-semibold text-glass-darkest mb-[10px] text-[3rem] lg:text-[2.5rem] md:text-[2rem]">
                {getCategoryTitle()}
              </h1>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section produits avec filtres */}
      <section className="py-[60px] lg:py-[50px] md:py-[40px] bg-gradient-to-b from-white to-glass-pearl">
        <Container fluid className="px-[20px] max-w-[1800px]">
          <Row className="items-start">
            {/* Sidebar Filtres - Gauche - Largeur réduite */}
            <Col lg={2} md={12} className="mb-[30px] lg:mb-0 lg:pr-[20px]">
                {/* Card Filtres */}
                <div className="bg-white rounded-[16px] shadow-[0_8px_32px_rgba(31,41,55,0.08)] overflow-hidden border border-glass-border">
                  {/* Header */}
                  <div className="px-[30px] py-[24px] bg-gradient-to-r from-glass-pearl to-white border-b border-glass-border">
                    <div className="flex items-center justify-between">
                      <h3 className="font-serif font-bold text-glass-darkest text-[1.2rem] m-0">
                        <i className="line-icon-Funnel mr-2 text-glass-accent"></i>
                        Filtres
                      </h3>
                      {activeFiltersCount > 0 && (
                        <button
                          onClick={resetFilters}
                          className="text-xs text-glass-accent hover:text-glass-darkest transition-colors font-semibold"
                        >
                          Réinitialiser
                        </button>
                      )}
                    </div>
                    {activeFiltersCount > 0 && (
                      <p className="text-xs text-glass-medium mt-1 mb-0">{activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''} actif{activeFiltersCount > 1 ? 's' : ''}</p>
                    )}
                  </div>

                  {/* Body */}
                  <div className="px-[30px] py-[30px]">
                    {/* Catégorie - Visible uniquement sur la page "Tout" */}
                    {categorie === 'tout' && (
                      <>
                        <div className="mb-[25px]">
                          <button
                            onClick={() => toggleFilterAccordion('category')}
                            className="w-full flex items-center justify-between mb-[15px] group"
                          >
                            <h4 className="font-serif font-semibold text-glass-darkest text-[0.95rem] m-0 flex items-center">
                              <span className="w-[4px] h-[16px] bg-glass-accent rounded-full mr-2"></span>
                              Catégorie
                            </h4>
                            <i className={`line-icon-Arrow-Down text-glass-medium transition-transform duration-300 ${filterAccordions.category ? 'rotate-180' : ''}`}></i>
                          </button>
                          {filterAccordions.category && (
                            <div className="space-y-[10px]">
                            {[
                              { label: 'Toutes', value: '*' },
                              { label: 'Vin', value: 'vin' },
                              { label: 'Bière', value: 'biere' },
                              { label: 'Spiritueux', value: 'spiritueux' },
                              { label: 'Bocaux', value: 'bocaux' },
                              { label: 'Soft drinks', value: 'soft-drinks' },
                              { label: 'Huiles et Sauces', value: 'huiles-sauces' },
                              { label: 'PET', value: 'pet' },
                              { label: 'Décorées', value: 'decorees' },
                            ].map((cat) => {
                              const isActive = filters.type === cat.value
                              return (
                                <button
                                  key={cat.value}
                                  onClick={() => handleFilterChange('type', cat.value)}
                                  className={`w-full text-left px-[18px] py-[12px] rounded-[10px] text-[0.95rem] font-medium transition-all flex items-center justify-between ${
                                    isActive
                                      ? 'bg-glass-accent text-white shadow-md'
                                      : 'bg-glass-offWhite text-glass-dark hover:bg-glass-pearl hover:shadow-sm'
                                  }`}
                                >
                                  <span>{cat.label}</span>
                                  {isActive && <i className="line-icon-Check text-[16px]"></i>}
                                </button>
                              )
                            })}
                            </div>
                          )}
                        </div>
                        {/* Divider */}
                        <div className="h-[1px] bg-glass-border mb-[25px]"></div>
                      </>
                    )}

                    {/* Couleur */}
                    <div className="mb-[25px]">
                      <button
                        onClick={() => toggleFilterAccordion('color')}
                        className="w-full flex items-center justify-between mb-[15px] group"
                      >
                        <h4 className="font-serif font-semibold text-glass-darkest text-[0.95rem] m-0 flex items-center">
                          <span className="w-[4px] h-[16px] bg-glass-accent rounded-full mr-2"></span>
                          Couleur
                        </h4>
                        <i className={`line-icon-Arrow-Down text-glass-medium transition-transform duration-300 ${filterAccordions.color ? 'rotate-180' : ''}`}></i>
                      </button>
                      {filterAccordions.color && (
                        <div className="space-y-[10px]">
                        {[
                          { label: 'Toutes', value: '*' },
                          { label: 'Blanc', value: 'Blanc' },
                          { label: 'Antique', value: 'Antique' },
                          { label: 'Brun', value: 'Brun' },
                          { label: 'Vert', value: 'Vert' },
                          { label: 'Transparent', value: 'Transparent' },
                        ].map((color) => {
                          const isActive = filters.color === color.value
                          return (
                            <button
                              key={color.value}
                              onClick={() => handleFilterChange('color', color.value)}
                              className={`w-full text-left px-[18px] py-[12px] rounded-[10px] text-[0.95rem] font-medium transition-all flex items-center justify-between ${
                                isActive
                                  ? 'bg-glass-accent text-white shadow-md'
                                  : 'bg-glass-offWhite text-glass-dark hover:bg-glass-pearl hover:shadow-sm'
                              }`}
                            >
                              <span>{color.label}</span>
                              {isActive && <i className="line-icon-Check text-[16px]"></i>}
                            </button>
                          )
                        })}
                        </div>
                      )}
                    </div>

                    {/* Divider */}
                    <div className="h-[1px] bg-glass-border mb-[25px]"></div>

                    {/* Capacité ras bord */}
                    <div>
                      <button
                        onClick={() => toggleFilterAccordion('capacity')}
                        className="w-full flex items-center justify-between mb-[15px] group"
                      >
                        <h4 className="font-serif font-semibold text-glass-darkest text-[0.95rem] m-0 flex items-center">
                          <span className="w-[4px] h-[16px] bg-glass-accent rounded-full mr-2"></span>
                          Capacité ras bord
                        </h4>
                        <i className={`line-icon-Arrow-Down text-glass-medium transition-transform duration-300 ${filterAccordions.capacity ? 'rotate-180' : ''}`}></i>
                      </button>

                      {filterAccordions.capacity && (
                        <div>

                      {/* Affichage de la plage */}
                      <div className="mb-[20px] text-center">
                        <div className="inline-flex items-center gap-2 px-[15px] py-[8px] bg-glass-pearl rounded-[8px]">
                          <span className="text-glass-darkest font-bold text-sm">{capacityRange[0]}ml</span>
                          <span className="text-glass-medium">-</span>
                          <span className="text-glass-darkest font-bold text-sm">{capacityRange[1]}ml</span>
                        </div>
                      </div>

                      {/* Range slider */}
                      <div className="relative h-[6px] bg-glass-pearl rounded-full mb-[10px]">
                        {/* Track actif */}
                        <div
                          className="absolute h-full bg-glass-accent rounded-full"
                          style={{
                            left: `${(capacityRange[0] / 2000) * 100}%`,
                            right: `${100 - (capacityRange[1] / 2000) * 100}%`
                          }}
                        ></div>

                        {/* Slider MIN */}
                        <input
                          type="range"
                          min="0"
                          max="2000"
                          step="50"
                          value={capacityRange[0]}
                          onChange={(e) => handleCapacityRangeChange(e, 0)}
                          className="slider-range"
                          style={{
                            zIndex: capacityRange[0] > 1500 ? 5 : 3
                          }}
                        />

                        {/* Slider MAX */}
                        <input
                          type="range"
                          min="0"
                          max="2000"
                          step="50"
                          value={capacityRange[1]}
                          onChange={(e) => handleCapacityRangeChange(e, 1)}
                          className="slider-range"
                          style={{
                            zIndex: 4
                          }}
                        />
                      </div>

                      {/* Labels min/max */}
                      <div className="flex justify-between text-xs text-glass-medium">
                        <span>0ml</span>
                        <span>2000ml</span>
                      </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Info produits filtrés */}
                <div className="mt-[20px] px-[15px] py-[12px] bg-white rounded-[12px] border border-glass-border text-center">
                  <p className="text-sm text-glass-medium mb-0">
                    <span className="font-bold text-glass-accent">{filteredProducts.length}</span> produit{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}
                  </p>
                </div>
            </Col>

            {/* Produits - Droite - Plus d'espace */}
            <Col lg={10} md={12} className="lg:pl-[20px]">
              {loading ? (
                <div className="text-center py-20">
                  <div style={{
                    display: 'inline-block',
                    width: '40px',
                    height: '40px',
                    border: '4px solid #E5E7EB',
                    borderTop: '4px solid #3B82F6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <p className="text-lg text-glass-dark mt-4">Chargement des produits...</p>
                </div>
              ) : filteredProducts.length > 0 ? (
                <>
                  <PortfolioOverlay
                    overlay={["#3B82F6cc", "#60A5FAcc"]}
                    grid="grid grid-4col xl-grid-4col lg-grid-4col md-grid-3col sm-grid-2col xs-grid-1col gutter-large"
                    filterData={undefined}
                    data={displayedProducts}
                  />

                  {/* Infinite scroll trigger & loader */}
                  {hasMoreProducts && (
                    <div ref={loaderRef} className="text-center py-10">
                      {loadingMore ? (
                        <div className="inline-flex items-center gap-3">
                          <div style={{
                            display: 'inline-block',
                            width: '24px',
                            height: '24px',
                            border: '3px solid #E5E7EB',
                            borderTop: '3px solid #3B82F6',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></div>
                          <span className="text-glass-medium text-sm">Chargement...</span>
                        </div>
                      ) : (
                        <span className="text-glass-medium text-sm">Scroll pour voir plus</span>
                      )}
                    </div>
                  )}

                  {/* Compteur de produits affichés */}
                  <div className="text-center py-4 border-t border-glass-border mt-6">
                    <p className="text-sm text-glass-medium mb-0">
                      <span className="font-bold text-glass-accent">{displayedProducts.length}</span> sur <span className="font-bold">{filteredProducts.length}</span> produits affichés
                    </p>
                  </div>
                </>
              ) : (
                <div className="bg-white border border-glass-border rounded-[16px] shadow-sm" style={{
                  textAlign: 'center',
                  padding: '60px 20px'
                }}>
                  <div className="mb-4">
                    <i className="line-icon-Sad text-[48px] text-glass-medium"></i>
                  </div>
                  <h3 className="text-glass-darkest font-serif font-bold text-[1.3rem] mb-[10px]">
                    Aucun produit trouvé
                  </h3>
                  <p className="text-glass-medium text-[0.95rem] mb-[25px]">
                    Essayez de modifier vos critères de filtrage
                  </p>
                  <button
                    onClick={resetFilters}
                    className="px-[30px] py-[12px] bg-glass-accent text-white rounded-[8px] hover:bg-glass-darkest transition-all font-semibold text-sm"
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Style pour les animations et sliders */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          0% { opacity: 0.5; }
          100% { opacity: 1; }
        }

        /* Style pour les range sliders */
        .slider-range {
          position: absolute;
          width: 100%;
          height: 8px;
          appearance: none;
          background: transparent;
          pointer-events: none;
          top: 0;
          left: 0;
        }

        .slider-range::-webkit-slider-thumb {
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #3B82F6;
          cursor: pointer;
          border: 4px solid white;
          box-shadow: 0 3px 8px rgba(59, 130, 246, 0.3);
          pointer-events: all;
          transition: all 0.2s ease;
        }

        .slider-range::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #3B82F6;
          cursor: pointer;
          border: 4px solid white;
          box-shadow: 0 3px 8px rgba(59, 130, 246, 0.3);
          pointer-events: all;
          transition: all 0.2s ease;
        }

        .slider-range::-webkit-slider-thumb:hover {
          background: #2563EB;
          transform: scale(1.15);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .slider-range::-moz-range-thumb:hover {
          background: #2563EB;
          transform: scale(1.15);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .slider-range::-webkit-slider-thumb:active {
          transform: scale(1.05);
        }

        .slider-range::-moz-range-thumb:active {
          transform: scale(1.05);
        }
      `}</style>

      {/* Footer */}
      <RDPFooter />
    </div>
  )
}

export default ProductsPage
